import { v4 as uuidv4 } from 'uuid';
import { getDefaultQuestion } from './types.js';
import { defaultQuestions } from './surveyStoreDefaults.js';

export const createQuestionsSlice = (set, get) => ({
    questions: [...defaultQuestions],
    selectedQuestionId: null,

    // Validation function that can be used by components
    validateName: (name, excludeQuestionId = null) => {
        const state = get();

        if (!name || !name.trim()) {
            return { isValid: false, error: 'Name is required' };
        }

        // Check if name exists in other questions (excluding specified question)
        const nameExists = state.questions.some(q =>
            q.internalId !== excludeQuestionId && q.name === name
        );

        if (nameExists) {
            return {
                isValid: false,
                error: 'This name already exists in the survey. Please choose a unique name.'
            };
        }

        return { isValid: true, error: '' };
    },

    addQuestion: (type, index = null) => {
        set((state) => {
            // Generate unique name based on type
            const existingNames = state.questions.map(q => q.name);
            let counter = 1;
            let newName = `${type}${counter}`;

            // Increment counter until we find a unique name
            while (existingNames.includes(newName)) {
                counter++;
                newName = `${type}${counter}`;
            }

            const newQuestion = {
                ...getDefaultQuestion(type),
                internalId: uuidv4(),
                name: newName
            };

            const questions = [...state.questions];
            if (index !== null && index >= 0 && index <= questions.length) {
                questions.splice(index, 0, newQuestion);
            } else {
                questions.push(newQuestion);
            }

            // Update sequence numbers for all questions
            const updatedQuestions = questions.map((q, idx) => ({
                ...q,
                sequenceNumber: idx + 1
            }));

            return {
                questions: updatedQuestions,
                selectedQuestionId: newQuestion.internalId,
                isDirty: true,
            };
        });
    },

    updateQuestion: (questionId, updates) =>
        set((state) => ({
            questions: state.questions.map((q) =>
                q.internalId === questionId ? { ...q, ...updates } : q
            ),
            isDirty: true,
        })),

    moveQuestion: (questionId, direction) =>
        set((state) => {
            const questionIndex = state.questions.findIndex((q) => q.internalId === questionId);
            if (questionIndex === -1) return state;

            const questions = [...state.questions];
            
            if (direction === 'up' && questionIndex > 0) {
                [questions[questionIndex], questions[questionIndex - 1]] = 
                [questions[questionIndex - 1], questions[questionIndex]];
            } else if (direction === 'down' && questionIndex < questions.length - 1) {
                [questions[questionIndex], questions[questionIndex + 1]] = 
                [questions[questionIndex + 1], questions[questionIndex]];
            } else {
                return state;
            }

            // Update sequence numbers for all questions
            const updatedQuestions = questions.map((q, idx) => ({
                ...q,
                sequenceNumber: idx + 1
            }));

            return {
                questions: updatedQuestions,
                isDirty: true,
            };
        }),

    removeQuestion: (questionId) =>
        set((state) => {
            const filteredQuestions = state.questions.filter((q) => q.internalId !== questionId);
            // Update sequence numbers for remaining questions
            const updatedQuestions = filteredQuestions.map((q, idx) => ({
                ...q,
                sequenceNumber: idx + 1
            }));
            return {
                questions: updatedQuestions,
                selectedQuestionId:
                    state.selectedQuestionId === questionId ? null : state.selectedQuestionId,
                isDirty: true,
            };
        }),

    selectQuestion: (questionId) => set({ selectedQuestionId: questionId }),
    clearSelection: () => set({ selectedQuestionId: null }),
    getSelectedQuestion: () => {
        const state = get();
        return state.questions.find((q) => q.internalId === state.selectedQuestionId) || null;
    },
    getQuestionById: (questionId) => {
        const state = get();
        return state.questions.find((q) => q.internalId === questionId) || null;
    },
    duplicateQuestion: (questionId) =>
        set((state) => {
            const questionIndex = state.questions.findIndex((q) => q.internalId === questionId);
            if (questionIndex === -1) return state;

            const originalQuestion = state.questions[questionIndex];

            // Generate unique name for the duplicated question
            const existingNames = state.questions.map(q => q.name);
            let counter = 1;
            let newName = `${originalQuestion.type}${counter}`;

            while (existingNames.includes(newName)) {
                counter++;
                newName = `${originalQuestion.type}${counter}`;
            }

            const duplicatedQuestion = {
                ...originalQuestion,
                internalId: uuidv4(),
                id: null, // Reset server ID for new question
                name: newName,
                title: `${originalQuestion.title} (Copy)`,
            };

            // If question has options, duplicate them with new IDs
            if (duplicatedQuestion.options) {
                duplicatedQuestion.options = duplicatedQuestion.options.map((option) => ({
                    ...option,
                    id: uuidv4(),
                }));
            }

            const questions = [...state.questions];
            questions.splice(questionIndex + 1, 0, duplicatedQuestion);

            // Update sequence numbers for all questions
            const updatedQuestions = questions.map((q, idx) => ({
                ...q,
                sequenceNumber: idx + 1
            }));

            return {
                questions: updatedQuestions,
                selectedQuestionId: duplicatedQuestion.internalId,
                isDirty: true,
            };
        }),

});